import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  CampaignRounded,
  DeleteOutlineRounded,
  EditNoteRounded,
  RestartAltRounded,
  SaveRounded,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';
import { useAuth } from '../../../contexts/auth.context';
import {
  courseTemplateApi,
  syllabusProposalApi,
  type StudentSyllabusDto,
  type SyllabusOverridePath,
  type TextbookDto,
} from '../../../lib/api/course-template.api';
import { SECTION_ONLY_OVERRIDE_PATHS } from '@dosfilos/application/composition';
import { PageEditor } from './PageEditor';
import { RepeatableListEditor } from './RepeatableListEditor';
import { ReadingsEditor, type TemplateState } from './CourseTemplateEditor';
import { errorText } from '../../../lib/error-text';
import { hasRichText } from '../../../lib/syllabus-text';

export type SyllabusEditSection =
  | 'description'
  | 'grading'
  | 'readings'
  | 'policies'
  | 'assignmentDetails'
  | 'calendar';

interface Props {
  open: SyllabusEditSection | null;
  sectionId: string;
  data: StudentSyllabusDto;
  onClose: () => void;
  /** Switches to another edit dialog — the calendar sends the teacher to the
   *  readings editor, which is where readings are changed one by one. */
  onOpenSection?: (section: SyllabusEditSection) => void;
  /** Called after any override write so the parent refetches the composed doc. */
  onSaved: () => void;
  /** F5: enables "Propose to the template" — needs the template this section
   *  was generated from, plus a readable label for the academic's inbox. */
  templateId?: string;
  sectionLabel?: string;
}

type Objective = { id: string; code?: string; text: string };
type EvaluationEdit = {
  id: string;
  name: string;
  weight: number;
  hours?: number;
  objectiveIds?: string[];
  description?: Record<string, unknown>;
};
type ReweightSummary = { reweighted: number; zeroed: number; withoutTasks: string[] };
type Evaluation = { id: string; name: string; description?: Record<string, unknown> };

const EMPTY_DOC: Record<string, unknown> = { type: 'doc', content: [{ type: 'paragraph' }] };

const isEmptyDoc = (doc: Record<string, unknown> | null | undefined): boolean => !hasRichText(doc);
type Readings = TemplateState['readings'];
interface LatePolicy {
  firstWindowHours: number;
  firstWindowPenalty: number;
  secondWindowHours: number;
  secondWindowPenalty: number;
  dailyPenaltyAfter: number;
}

const EMPTY_LATE: LatePolicy = {
  firstWindowHours: 24,
  firstWindowPenalty: 10,
  secondWindowHours: 72,
  secondWindowPenalty: 25,
  dailyPenaltyAfter: 10,
};

/** F2 edit dialogs for the /curso Syllabus tab. Every save writes a SECTION
 *  override (never the template); "Restaurar de la plantilla" clears it so the
 *  field composes live again. */
export function SyllabusOverrideDialogs({
  open,
  sectionId,
  data,
  onClose,
  onOpenSection,
  onSaved,
  templateId,
  sectionLabel,
}: Props): JSX.Element | null {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Paths cuya propuesta se está confirmando (null = sin diálogo abierto). */
  const [proposing, setProposing] = useState<SyllabusOverridePath[] | null>(null);
  const [proposed, setProposed] = useState(false);
  const { user } = useAuth();

  const ov = (data.sectionOverrides ?? {}) as Record<string, unknown>;
  const tpl = data.template;

  // Effective initial values: override ?? template. (When the template field is
  // empty the displayed value may come from inheritance — the caption in each
  // dialog says so.)
  const initialDescription =
    (ov['description'] as Record<string, unknown> | undefined) ??
    (tpl.description as Record<string, unknown>) ??
    null;
  const initialOacs =
    (ov['objectives.course'] as Objective[] | undefined) ??
    (tpl.objectives as { course?: Objective[] })?.course ??
    [];
  const initialGradingNote =
    (ov['grading.note'] as Record<string, unknown> | undefined) ??
    (tpl.grading as { note?: Record<string, unknown> })?.note ??
    null;
  const initialReadings = (ov['readings'] ?? tpl.readings) as Readings;
  const initialPoliciesNote =
    (ov['policiesNote'] as string | undefined) ?? ((tpl['policiesNote'] as string) || '');
  // "Detalles de la tarea": the intro prose, and each evaluation's description.
  // The section override stores ONLY the evaluations it changed (id → text, or
  // null to remove it here), so an untouched one keeps following the template.
  const initialAssignmentsNote =
    (ov['assignmentsNote'] as Record<string, unknown> | undefined) ??
    (tpl['assignmentsNote'] as Record<string, unknown> | undefined) ??
    null;
  const templateEvaluations = (
    (tpl.grading as { components?: Evaluation[] } | undefined)?.components ?? []
  ).map((c) => ({ id: c.id, name: c.name, description: c.description ?? null }));
  const overriddenDescriptions = (ov['grading.descriptions'] ?? {}) as Record<
    string,
    Record<string, unknown> | null
  >;
  const initialDescriptions: Record<string, Record<string, unknown> | null> = Object.fromEntries(
    templateEvaluations.map((c) => [
      c.id,
      c.id in overriddenDescriptions ? overriddenDescriptions[c.id]! : c.description,
    ]),
  );
  // The section's evaluation table: the override when there is one, else the
  // template's. The weights here drive the task weights (server-side).
  const initialComponents: EvaluationEdit[] = (
    (ov['grading.components'] as EvaluationEdit[] | undefined) ??
    (tpl.grading as { components?: EvaluationEdit[] } | undefined)?.components ??
    []
  ).map((c) => ({ ...c }));
  const initialShowReadings =
    (ov['calendar.showReadings'] as boolean | undefined) ??
    (tpl['calendarShowsReadings'] as boolean | undefined) ??
    true;
  const overriddenLate = ov['latePolicy'] as LatePolicy | undefined;
  const templateLate = tpl['latePolicy'] as LatePolicy | undefined;

  const [description, setDescription] = useState<Record<string, unknown> | null>(null);
  const [oacs, setOacs] = useState<Objective[]>([]);
  const [gradingNote, setGradingNote] = useState<Record<string, unknown> | null>(null);
  const [readings, setReadings] = useState<Readings | null>(null);
  const [policiesNote, setPoliciesNote] = useState('');
  const [customLate, setCustomLate] = useState(false);
  const [late, setLate] = useState<LatePolicy>(EMPTY_LATE);
  const [textbooks, setTextbooks] = useState<TextbookDto[]>([]);
  const [assignmentsNote, setAssignmentsNote] = useState<Record<string, unknown> | null>(null);
  const [descriptions, setDescriptions] = useState<Record<string, Record<string, unknown> | null>>(
    {},
  );
  const [showReadings, setShowReadings] = useState(true);
  const [components, setComponents] = useState<EvaluationEdit[]>([]);
  const [reweightSummary, setReweightSummary] = useState<ReweightSummary | null>(null);

  // Reset drafts each time a dialog opens.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setDescription(initialDescription);
    setOacs(initialOacs);
    setGradingNote(initialGradingNote);
    setReadings(initialReadings);
    setPoliciesNote(initialPoliciesNote);
    setCustomLate(overriddenLate !== undefined);
    setLate(overriddenLate ?? templateLate ?? EMPTY_LATE);
    setAssignmentsNote(initialAssignmentsNote);
    setDescriptions(initialDescriptions);
    setShowReadings(initialShowReadings);
    setComponents(initialComponents);
    setReweightSummary(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Textbook catalog for the readings dialog (same source as the template editor).
  useEffect(() => {
    if (open !== 'readings' || !currentTenantId || textbooks.length > 0) return;
    void (async () => {
      const snap = await getDocs(collection(db, `tenants/${currentTenantId}/textbooks`));
      setTextbooks(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }) as TextbookDto & { deletedAt?: unknown })
          .filter((tb) => !tb.deletedAt),
      );
    })();
  }, [open, currentTenantId, textbooks.length]);

  if (!open) return null;

  const write = async (
    path: SyllabusOverridePath,
    value: unknown,
  ): Promise<{ reweight?: ReweightSummary } | undefined> => {
    if (!currentTenantId) return undefined;
    return courseTemplateApi.setSyllabusContentOverride({
      tenantId: currentTenantId,
      sectionId,
      path,
      value,
    });
  };

  const run = async (fn: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onSaved();
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  // F5: proponer al académico que lo editado en esta sección entre a la
  // plantilla. Sólo tiene sentido sobre un campo que ESTA sección cambió, así
  // que comparte la condición de visibilidad con "Restaurar de la plantilla".
  const proposeButton = (paths: SyllabusOverridePath[]): JSX.Element | null => {
    // A section-only path (the evaluation table) is never proposed to the template.
    const active = paths.filter(
      (p) => p in ov && !(SECTION_ONLY_OVERRIDE_PATHS as readonly string[]).includes(p),
    );
    if (active.length === 0 || !templateId || !sectionLabel) return null;
    return (
      <Button
        color="info"
        startIcon={<CampaignRounded />}
        disabled={busy}
        onClick={() => setProposing(active)}
      >
        {t('lms.syllabusTab.propose.action')}
      </Button>
    );
  };

  const restoreButton = (paths: SyllabusOverridePath[]): JSX.Element | null => {
    const active = paths.filter((p) => p in ov);
    if (active.length === 0) return null;
    return (
      <Button
        color="warning"
        startIcon={<RestartAltRounded />}
        disabled={busy}
        onClick={() =>
          void run(async () => {
            for (const p of active) await write(p, null);
          })
        }
      >
        {t('lms.syllabusTab.edit.restore')}
      </Button>
    );
  };

  const saveChanged = (pairs: [SyllabusOverridePath, unknown, unknown][]) =>
    void run(async () => {
      for (const [path, draft, initial] of pairs) {
        if (JSON.stringify(draft) !== JSON.stringify(initial)) await write(path, draft);
      }
    });

  const common = {
    open: true,
    onClose: busy ? undefined : onClose,
    maxWidth: 'md' as const,
    fullWidth: true,
  };
  const actions = (onSave: () => void, restorePaths: SyllabusOverridePath[]) => (
    <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {proposeButton(restorePaths)}
      {restoreButton(restorePaths)}
      <Button onClick={onClose} disabled={busy}>
        {t('lms.syllabusTab.edit.cancel')}
      </Button>
      <Button
        variant="contained"
        onClick={onSave}
        disabled={busy}
        startIcon={busy ? <CircularProgress size={14} /> : <SaveRounded />}
      >
        {t('lms.syllabusTab.edit.save')}
      </Button>
    </DialogActions>
  );

  const scopeNote = (
    <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
      {t('lms.syllabusTab.edit.scopeNote')}
    </Alert>
  );

  const sendProposals = async (): Promise<void> => {
    if (!currentTenantId || !templateId || !sectionLabel || !proposing) return;
    setBusy(true);
    setError(null);
    try {
      for (const path of proposing) {
        await syllabusProposalApi.propose({
          tenantId: currentTenantId,
          sectionId,
          templateId,
          sectionLabel,
          actorName: user?.displayName || user?.email || 'Docente',
          path,
          value: ov[path],
        });
      }
      setProposed(true);
      setProposing(null);
    } catch (e) {
      setError(errorText(e));
      setProposing(null);
    } finally {
      setBusy(false);
    }
  };

  // Confirmación: deja explícito que la plantilla NO cambia hasta que el
  // académico apruebe — es la diferencia entre proponer y editar.
  const proposeDialog = proposing && (
    <Dialog open onClose={busy ? undefined : () => setProposing(null)} maxWidth="sm" fullWidth>
      <DialogTitle>{t('lms.syllabusTab.propose.confirmTitle')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2">{t('lms.syllabusTab.propose.confirmBody')}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setProposing(null)} disabled={busy}>
          {t('lms.syllabusTab.edit.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => void sendProposals()}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} /> : <CampaignRounded />}
        >
          {t('lms.syllabusTab.propose.confirmAction')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const proposedToast = proposed && (
    <Dialog open onClose={() => setProposed(false)} maxWidth="xs" fullWidth>
      <DialogContent>
        <Alert severity="success">{t('lms.syllabusTab.propose.sent')}</Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setProposed(false)}>{t('lms.syllabusTab.edit.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );

  if (open === 'description') {
    return (
      <>
        <Dialog {...common}>
          <DialogTitle>{t('lms.syllabusTab.edit.descriptionTitle')}</DialogTitle>
          <DialogContent dividers>
            {scopeNote}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Typography variant="subtitle2" fontWeight={700} mb={1}>
              {t('lms.syllabusTab.edit.descriptionField')}
            </Typography>
            <PageEditor value={description} onChange={setDescription} compact />
            <Stack mt={3}>
              <RepeatableListEditor
                label={t('lms.syllabusTab.edit.oacsField')}
                items={oacs}
                onChange={setOacs}
                autoCodePrefix="OAC"
              />
            </Stack>
          </DialogContent>
          {actions(
            () =>
              saveChanged([
                ['description', description, initialDescription],
                ['objectives.course', oacs, initialOacs],
              ]),
            ['description', 'objectives.course'],
          )}
        </Dialog>
        {proposeDialog}
        {proposedToast}
      </>
    );
  }

  if (open === 'grading') {
    const total = components.reduce((n, c) => n + (Number(c.weight) || 0), 0);
    const updateRow = (i: number, patch: Partial<EvaluationEdit>): void =>
      setComponents((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    // Not `run`: after the table is saved the dialog stays open to show how the
    // task weights followed it — that summary is the point of this dialog.
    const saveGrading = async (): Promise<void> => {
      setBusy(true);
      setError(null);
      try {
        if (JSON.stringify(gradingNote) !== JSON.stringify(initialGradingNote)) {
          await write('grading.note', gradingNote);
        }
        let summary: ReweightSummary | undefined;
        if (JSON.stringify(components) !== JSON.stringify(initialComponents)) {
          const cleaned = components.map((c) => ({
            ...c,
            name: c.name.trim(),
            weight: Number(c.weight) || 0,
            ...(c.hours !== undefined ? { hours: Number(c.hours) || 0 } : {}),
          }));
          summary = (await write('grading.components', cleaned))?.reweight;
        }
        onSaved();
        if (summary) setReweightSummary(summary);
        else onClose();
      } catch (e) {
        setError(errorText(e));
      } finally {
        setBusy(false);
      }
    };
    return (
      <>
        <Dialog {...common}>
          <DialogTitle>{t('lms.syllabusTab.edit.gradingTitle')}</DialogTitle>
          <DialogContent dividers>
            {scopeNote}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {reweightSummary ? (
              <Alert severity="success">
                {t('lms.syllabusTab.edit.reweightDone', { count: reweightSummary.reweighted })}
                {reweightSummary.zeroed > 0 &&
                  ` ${t('lms.syllabusTab.edit.reweightZeroed', { count: reweightSummary.zeroed })}`}
                {reweightSummary.withoutTasks.length > 0 &&
                  ` ${t('lms.syllabusTab.edit.reweightWithoutTasks', {
                    names: reweightSummary.withoutTasks.join(', '),
                  })}`}
              </Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" mb={1.5}>
                  {t('lms.syllabusTab.edit.componentsHelp')}
                </Typography>
                <Stack spacing={1}>
                  {components.map((c, i) => (
                    <Stack key={c.id} direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        label={t('lms.syllabusTab.edit.componentName')}
                        value={c.name}
                        onChange={(e) => updateRow(i, { name: e.target.value })}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label={t('lms.syllabusTab.edit.componentWeight')}
                        value={c.weight}
                        onChange={(e) => updateRow(i, { weight: Number(e.target.value) })}
                        sx={{ width: 110 }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label={t('lms.syllabusTab.edit.componentHours')}
                        value={c.hours ?? ''}
                        onChange={(e) =>
                          updateRow(i, {
                            hours: e.target.value === '' ? 0 : Number(e.target.value),
                          })
                        }
                        sx={{ width: 110 }}
                      />
                      <IconButton
                        aria-label={t('lms.syllabusTab.edit.componentRemove')}
                        onClick={() => setComponents((rows) => rows.filter((_, j) => j !== i))}
                      >
                        <DeleteOutlineRounded fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1.5}>
                  <Button
                    size="small"
                    startIcon={<AddRounded />}
                    onClick={() =>
                      setComponents((rows) => [
                        ...rows,
                        { id: `sec-${Date.now().toString(36)}`, name: '', weight: 0 },
                      ])
                    }
                  >
                    {t('lms.syllabusTab.edit.componentAdd')}
                  </Button>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color={Math.abs(total - 100) < 0.01 ? 'success.main' : 'error.main'}
                  >
                    {t('lms.syllabusTab.edit.componentsTotal', {
                      total: Math.round(total * 100) / 100,
                    })}
                  </Typography>
                </Stack>
                <Typography variant="subtitle2" fontWeight={700} mt={3} mb={1}>
                  {t('lms.syllabusTab.edit.gradingNoteField')}
                </Typography>
                <PageEditor value={gradingNote} onChange={setGradingNote} compact />
              </>
            )}
          </DialogContent>
          {reweightSummary ? (
            <DialogActions>
              <Button variant="contained" onClick={onClose}>
                {t('lms.syllabusTab.edit.close')}
              </Button>
            </DialogActions>
          ) : (
            actions(() => void saveGrading(), ['grading.note', 'grading.components'])
          )}
        </Dialog>
        {proposeDialog}
        {proposedToast}
      </>
    );
  }

  if (open === 'assignmentDetails') {
    // Only the evaluations whose text differs from the template are stored; an
    // emptied editor means "remove it in this section" (null).
    const saveDetails = (): void =>
      void run(async () => {
        const note = isEmptyDoc(assignmentsNote) ? null : assignmentsNote;
        const initialNote = isEmptyDoc(initialAssignmentsNote) ? null : initialAssignmentsNote;
        if (JSON.stringify(note) !== JSON.stringify(initialNote)) {
          // An emptied intro is stored as an empty doc, not as null: null would
          // CLEAR the override and bring the template's intro back.
          await write('assignmentsNote', note ?? EMPTY_DOC);
        }
        const diff: Record<string, Record<string, unknown> | null> = {};
        for (const c of templateEvaluations) {
          const draft = isEmptyDoc(descriptions[c.id] ?? null) ? null : descriptions[c.id]!;
          if (JSON.stringify(draft) !== JSON.stringify(c.description)) diff[c.id] = draft;
        }
        if (JSON.stringify(diff) !== JSON.stringify(overriddenDescriptions)) {
          await write('grading.descriptions', Object.keys(diff).length > 0 ? diff : null);
        }
      });
    return (
      <>
        <Dialog {...common}>
          <DialogTitle>{t('lms.syllabusTab.edit.assignmentDetailsTitle')}</DialogTitle>
          <DialogContent dividers>
            {scopeNote}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary" mb={2}>
              {t('lms.syllabusTab.edit.assignmentDetailsHelp')}
            </Typography>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>
              {t('lms.syllabusTab.edit.assignmentDetailsIntro')}
            </Typography>
            <PageEditor value={assignmentsNote} onChange={setAssignmentsNote} compact />
            {templateEvaluations.map((c) => (
              <Stack key={c.id} mt={3}>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                  {c.name}
                </Typography>
                <PageEditor
                  value={descriptions[c.id] ?? null}
                  onChange={(v) => setDescriptions((d) => ({ ...d, [c.id]: v }))}
                  compact
                />
              </Stack>
            ))}
          </DialogContent>
          {actions(saveDetails, ['assignmentsNote', 'grading.descriptions'])}
        </Dialog>
        {proposeDialog}
        {proposedToast}
      </>
    );
  }

  if (open === 'calendar') {
    return (
      <>
        <Dialog {...common} maxWidth="sm">
          <DialogTitle>{t('lms.syllabusTab.edit.calendarTitle')}</DialogTitle>
          <DialogContent dividers>
            {scopeNote}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {/* The calendar and the reading plan are ONE list of readings. Changing
                some of them happens in the readings editor; this switch is only
                the all-or-nothing presentation choice. */}
            <Typography variant="body2" mb={1.5}>
              {t('lms.syllabusTab.edit.calendarOneList')}
            </Typography>
            {onOpenSection && (
              <Button
                variant="outlined"
                startIcon={<EditNoteRounded />}
                disabled={busy}
                onClick={() => onOpenSection('readings')}
                sx={{ mb: 2.5 }}
              >
                {t('lms.syllabusTab.edit.calendarEditReadings')}
              </Button>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={!showReadings}
                  onChange={(e) => setShowReadings(!e.target.checked)}
                />
              }
              label={t('lms.syllabusTab.edit.calendarHideAllReadings')}
            />
            <Typography variant="body2" color="text.secondary" mt={1}>
              {t('lms.syllabusTab.edit.calendarHideAllReadingsHelp')}
            </Typography>
          </DialogContent>
          {actions(
            () => saveChanged([['calendar.showReadings', showReadings, initialShowReadings]]),
            ['calendar.showReadings'],
          )}
        </Dialog>
        {proposeDialog}
        {proposedToast}
      </>
    );
  }

  if (open === 'readings') {
    return (
      <>
        <Dialog {...common}>
          <DialogTitle>{t('lms.syllabusTab.edit.readingsTitle')}</DialogTitle>
          <DialogContent dividers>
            {scopeNote}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {readings && (
              <ReadingsEditor
                readings={readings}
                update={(r) => setReadings(r)}
                textbooks={textbooks}
              />
            )}
          </DialogContent>
          {actions(() => saveChanged([['readings', readings, initialReadings]]), ['readings'])}
        </Dialog>
        {proposeDialog}
        {proposedToast}
      </>
    );
  }

  // policies
  const lateField = (key: keyof LatePolicy, label: string): JSX.Element => (
    <TextField
      size="small"
      type="number"
      label={label}
      value={late[key]}
      onChange={(e) => setLate({ ...late, [key]: Number(e.target.value) })}
      sx={{ width: 170 }}
    />
  );
  return (
    <>
      <Dialog {...common}>
        <DialogTitle>{t('lms.syllabusTab.edit.policiesTitle')}</DialogTitle>
        <DialogContent dividers>
          {scopeNote}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            label={t('lms.syllabusTab.edit.policiesNoteField')}
            multiline
            minRows={5}
            fullWidth
            value={policiesNote}
            onChange={(e) => setPoliciesNote(e.target.value)}
          />
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Switch checked={customLate} onChange={(e) => setCustomLate(e.target.checked)} />
            }
            label={t('lms.syllabusTab.edit.customLate')}
          />
          {customLate && (
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap mt={1}>
              {lateField('firstWindowHours', t('lms.syllabusTab.edit.late.firstWindowHours'))}
              {lateField('firstWindowPenalty', t('lms.syllabusTab.edit.late.firstWindowPenalty'))}
              {lateField('secondWindowHours', t('lms.syllabusTab.edit.late.secondWindowHours'))}
              {lateField('secondWindowPenalty', t('lms.syllabusTab.edit.late.secondWindowPenalty'))}
              {lateField('dailyPenaltyAfter', t('lms.syllabusTab.edit.late.dailyPenaltyAfter'))}
            </Stack>
          )}
        </DialogContent>
        {actions(() => {
          void run(async () => {
            if (policiesNote !== initialPoliciesNote) await write('policiesNote', policiesNote);
            if (customLate) {
              if (JSON.stringify(late) !== JSON.stringify(overriddenLate)) {
                await write('latePolicy', late);
              }
            } else if (overriddenLate !== undefined) {
              // Switch turned off while an override exists → explicit revert.
              await write('latePolicy', null);
            }
          });
        }, ['policiesNote'])}
      </Dialog>
      {proposeDialog}
      {proposedToast}
    </>
  );
}
